import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { patientService } from '../services/patientService';
import { entitySyncService } from '../services/entitySyncService';

// Async thunks for API calls
export const fetchPatients = createAsyncThunk(
  'patients/fetchPatients',
  async (query, { rejectWithValue }) => {
    try {
      const response = await patientService.getAllPatients(query);
      return response.data ? response.data : response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const loadPatientsFromCache = createAsyncThunk(
  'patients/loadFromCache',
  async (_, { rejectWithValue }) => {
    try {
      const data = await entitySyncService.getLocalEntities('patients');
      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchPatientById = createAsyncThunk(
  'patients/fetchPatientById',
  async (patientId, { rejectWithValue }) => {
    try {
      const response = await patientService.getPatientById(patientId);
      return response.data ? response.data : response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
  {
    condition: (patientId, { getState }) => {
      const { patients } = getState();
      if (patients.loading || (patients.selectedPatient?.id === patientId && !patients.error)) {
        return false;
      }
    }
  }
);

export const createPatient = createAsyncThunk(
  'patients/createPatient',
  async (patientData, { rejectWithValue }) => {
    try {
      const defaultPatientData = {
        addressVerified: false,
        allergiesVerified: false,
        allergies: [],
        medicalConditions: [],
        ...patientData,
      };
      const response = await patientService.createPatient(defaultPatientData);
      return response.data ? response.data : response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const updatePatient = createAsyncThunk(
  'patients/updatePatient',
  async ({ patientId, updates }, { rejectWithValue }) => {
    try {
      const response = await patientService.updatePatient(patientId, updates);
      return response.data ? response.data : response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const deletePatient = createAsyncThunk(
  'patients/deletePatient',
  async (patientId, { rejectWithValue }) => {
    try {
      await patientService.deletePatient(patientId);
      return patientId;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchPatientSearchIndex = createAsyncThunk(
  'patients/fetchPatientSearchIndex',
  async (_, { rejectWithValue }) => {
    try {
      const response = await patientService.getSearchIndex();
      return response.data ? response.data : response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const addEntityAssociation = createAsyncThunk(
  'patients/addEntityAssociation',
  async ({ patientId, association }, { rejectWithValue }) => {
    try {
      const response = await patientService.addEntityAssociation(patientId, association);
      return response.data ? response.data : response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const markEntityAsCurrent = createAsyncThunk(
  'patients/markEntityAsCurrent',
  async ({ patientId, entityType, entityId }, { rejectWithValue }) => {
    try {
      const response = await patientService.markEntityAsCurrent(patientId, entityType, entityId);
      return response.data ? response.data : response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchEntityHistory = createAsyncThunk(
  'patients/fetchEntityHistory',
  async ({ patientId, entityType }, { rejectWithValue }) => {
    try {
      const response = await patientService.getEntityHistory(patientId, entityType);
      return response.data ? response.data : response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);


// Slice
const patientSlice = createSlice({
  name: 'patients',
  initialState: {
    patients: [],
    searchIndex: [],
    searchIndexLoading: false,
    searchIndexFetched: false,
    selectedPatient: null,
    loading: false,
    error: null,
    filters: {
      page: 1,
      limit: 10,
      firstName: '',
      lastName: '',
      contactPhone: '',
      dateOfBirth: null,
    },
    totalPages: 1,
    totalPatients: 0,
    currentListRequestId: null,
    entityHistory: [],
    entityHistoryLoading: false,
  },

  reducers: {
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    resetFilters: (state) => {
      state.filters = {
        page: 1,
        limit: 10,
        firstName: '',
        lastName: '',
        contactPhone: '',
        dateOfBirth: null,
      };
    },
    clearError: (state) => {
      state.error = null;
    },
    selectPatient: (state, action) => {
      state.selectedPatient = action.payload;
    },
    clearSelectedPatient: (state) => {
      state.selectedPatient = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch patients
      .addCase(fetchPatients.pending, (state, action) => {
        state.loading = true;
        state.error = null;
        state.currentListRequestId = action.meta.requestId;
      })
      .addCase(fetchPatients.fulfilled, (state, action) => {
        if (state.currentListRequestId !== action.meta.requestId) return;
        state.loading = false;
        state.currentListRequestId = null;
        state.patients = action.payload.data || action.payload;
        state.totalPages = action.payload.pagination.totalPages || 1;
        state.totalPatients = action.payload.pagination.totalItems || action.payload.length;
      })
      .addCase(fetchPatients.rejected, (state, action) => {
        if (state.currentListRequestId !== action.meta.requestId) return;
        state.loading = false;
        state.currentListRequestId = null;
        state.error = action.payload;
      })
      .addCase(loadPatientsFromCache.fulfilled, (state, action) => {
        state.patients = action.payload;
        state.totalPatients = action.payload.length;
        state.totalPages = Math.ceil(action.payload.length / state.filters.limit);
      })
      // Fetch patient search index
      .addCase(fetchPatientSearchIndex.pending, (state) => {
        state.searchIndexLoading = true;
      })
      .addCase(fetchPatientSearchIndex.fulfilled, (state, action) => {
        state.searchIndexLoading = false;
        state.searchIndex = action.payload;
        state.searchIndexFetched = true;
      })
      .addCase(fetchPatientSearchIndex.rejected, (state) => {
        state.searchIndexLoading = false;
        state.searchIndexFetched = true;
      })
      // Fetch patient by ID
      .addCase(fetchPatientById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPatientById.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedPatient = action.payload;
      })
      .addCase(fetchPatientById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Create patient
      .addCase(createPatient.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createPatient.fulfilled, (state, action) => {
        state.loading = false;
        state.patients.unshift(action.payload);
        state.totalPatients += 1;
        // Mirror to IndexedDB
        entitySyncService.upsertLocalEntity('patients', action.payload);
      })
      .addCase(createPatient.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Update patient
      .addCase(updatePatient.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updatePatient.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.patients.findIndex(p => p.id === action.payload.id);
        if (index !== -1) {
          state.patients[index] = action.payload;
        }
        if (state.selectedPatient?.id === action.payload.id) {
          state.selectedPatient = action.payload;
        }
        // Mirror to IndexedDB
        entitySyncService.upsertLocalEntity('patients', action.payload);
      })
      .addCase(updatePatient.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(deletePatient.fulfilled, (state, action) => {
        state.patients = state.patients.filter(p => p.id !== action.payload);
        // Mirror to IndexedDB
        entitySyncService.deleteLocalEntity('patients', action.payload);
      })
      // Add Entity Association
      .addCase(addEntityAssociation.fulfilled, (state, action) => {
        // Optimistic update or refetch could happen here. 
        // For now, if the response includes the updated patient, we could update selectedPatient
        if (state.selectedPatient && state.selectedPatient.id === action.payload.id) {
          state.selectedPatient = action.payload;
        }
      })
      // Mark Entity as Current
      .addCase(markEntityAsCurrent.fulfilled, (state, action) => {
        if (state.selectedPatient && state.selectedPatient.id === action.payload.id) {
          state.selectedPatient = action.payload;
        }
      })
      // Fetch Entity History
      .addCase(fetchEntityHistory.pending, (state) => {
        state.entityHistoryLoading = true;
      })
      .addCase(fetchEntityHistory.fulfilled, (state, action) => {
        state.entityHistoryLoading = false;
        state.entityHistory = action.payload;
      })
      .addCase(fetchEntityHistory.rejected, (state, action) => {
        state.entityHistoryLoading = false;
        state.error = action.payload;
      });

  },
});

export const { setFilters, resetFilters, clearError, selectPatient, clearSelectedPatient } = patientSlice.actions;
export default patientSlice.reducer;
